<script setup>
  import { ref, computed, defineAsyncComponent, getCurrentInstance } from 'vue'

  import termsOfUseGROK from '@/assets/grok/policy/_grok_terms_of_use.pdf'
  import termsOfUseARVAND from '@/assets/arvand/policy/oferta_2025.pdf'
  import { shortenString } from "@/utils/strings.js"
  import QRCodeVue3 from "qrcode-vue3";

  const cashModalTextHTML = `
    <h2>Пополнение счёта наличными в кассе РНКО «СТАТЕРА» (ООО)</h2>

    <p>Для пополнения счёта наличными необходимо лично обратиться в отделение РНКО «СТАТЕРА» (ООО) по адресу:</p>

    <address>
      384900, Республика Абхазия,<br>
      г. Сухум, ул. Лакоба, д. 34
    </address>

    <p>При обращении в кассу необходимо предоставить:</p>

    <ul>
      <li>Паспорт на имя владельца счёта.</li>
      <li>Информацию для оформления заявки:
        <ul>
          <li>Фамилия, имя, отчество владельца счёта;</li>
          <li>Сумма пополнения;</li>
          <li>Номер счёта или иные необходимые реквизиты.</li>
        </ul>
      </li>
    </ul>

    <p>Сотрудник кассы оформит заявку на основании предоставленных данных. После проверки документов будет осуществлено пополнение счёта наличными.</p>

    <p><strong>Важно:</strong> паспортные данные должны совпадать с информацией, указанной в электронном кошельке.</p>
  `

  const BaseButton = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BaseButton.vue" */ '@/components/base/buttons/BaseButton.vue')
  )
  const BackBtn = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/buttons/BackBtn.vue" */ '@/components/base/buttons/BackBtn.vue')
  )
  const Copy = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/base/clipboardCopy/copy.vue" */ '@/components/base/clipboardCopy/copy.vue')
  )
  const TextModal = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/TextModal.vue" */ '@/components/TextModal.vue')
  )
  const WalletList = defineAsyncComponent(() =>
      import(/* webpackChunkName: "components/WalletList.vue" */ '@/components/WalletList.vue')
  )

  import { useAuthStore } from '@/store/stores/auth'
  const authStore = useAuthStore()

  import { useStore } from 'vuex'
  const vuexStore = useStore()

  import { settings } from "../../appType/settings.js";
  const envName = settings.env.VUE_APP_ENV_NAME

  const instance = getCurrentInstance();
  const $http = instance.proxy.$http
  const text = instance.proxy.text

  const address = '0x10754Db3730f37F3d696c04e0e984039A1363DfB'
  const amount = ref('')
  const errorMessage = ref('')
  const cashModalActive = ref(false)
  const cashModalText = cashModalTextHTML
  const termsAccepted = ref(true)
  const loading = ref(false)

  const activeWallet = computed(() => {
    return vuexStore.state.currentWallet
  })
  const activeCurrency = computed(() => {
    return  activeWallet.value?.currency_code
  })
  const dynamicTermsLink = computed(() => {
    if (envName === 'arvand') {
      return termsOfUseARVAND
    } else if (envName === 'grok') {
      return termsOfUseGROK
    }
    return null
  })

  const termsModel = computed({
    get() {
      return termsAccepted.value = ![null, 'false'].includes(localStorage.getItem('termsAccepted'))
    },
    set(v) {
      localStorage.setItem('termsAccepted', v)
      termsAccepted.value = Boolean(v)
    }
  })

  const authToken = computed(() => {
    return vuexStore.state.authToken
  })

  const topUpDisabled = computed(() => {
    return loading.value || !Number(amount.value)
  })

  const TopUpFromBank = async ()=> {
    if (!amount.value) {
      errorMessage.value = text('wallet_topup.error_empty_amount')
      return
    }  else {
      errorMessage.value = ''
    }

    loading.value = true

    const { data } = await $http.post(
        '/v2/wallet/top-up',
        {
          "amount": amount.value,
          "currency_code": activeWallet.value.currency_code
        },
        {
          "headers": {
            "Authorization": "Token " + authToken.value
          },
          validateStatus: () => true
        }
    );

    if (data.status === "error") {
      errorMessage.value = data.message
      // errorMessage.value = text('wallet_topup.error')
      // errorMessage.value = text('wallet_topup.error_exceed_limit')
      loading.value = false
    } else {
      if (authStore.appType === 'web') {
        window.open(data.url, '_blank')
      } else {
        window.location = data.url
      }
    }
  }
</script>

<template>
  <div class="wallet_topup">
    <div>
      <div class="title">
        <div class="back-btn__wrapper">
          <BackBtn @click="$router.back()"/>
        </div>
        {{ text('wallet_topup.select_account') }}
      </div>
      <WalletList />
      <div v-if="activeCurrency !== 'USDC'" class="input">
        <span>{{ text('wallet_topup.amount') }}</span>
        <input type="text" v-model="amount" inputmode="numeric" pattern="[0-9\s]{13,19}" placeholder="0">
      </div>
    </div>
    <div v-if="errorMessage" class="error">{{ errorMessage }}</div>

    <div v-if="envName !== 'grok'">
      <div v-if="activeCurrency !== 'USDC'" class="divider" />
      <div class="actions-block">
        <BaseButton prefix grayscale-accent @click="TopUpFromBank">
          <template #prefix>
            <img height="24" src="@/assets/images/sbp_logo.svg" alt="sbp" />
          </template>
          <template #label>
            {{ text('wallet_topup.methods.cbp') }}
          </template>
        </BaseButton>
        <BaseButton v-if="envName === 'stater'" grayscale-accent prefix @click="cashModalActive = true">
          <template #prefix>
            <img height="24" src="@/assets/images/cash.svg" alt="sbp" />
          </template>
          <template #label>
            {{ text('wallet_topup.methods.cash') }}
          </template>
        </BaseButton>
      </div>
      <TextModal
          @closeModal="cashModalActive = false"
          v-if="cashModalActive"
          :title="text('wallet_topup.modal.title')"
          :text="cashModalText"
      />
    </div>
    <div v-else>
      <!--            <div class="payment_security_section">-->
      <!--                <div class="payment_methods">-->
      <!--                    <img src="@/assets/images/visa_logo.svg" alt="Visa" />-->
      <!--                    <img src="@/assets/images/mastercard_logo.svg" alt="MasterCard" />-->
      <!--                </div>-->
      <!--            </div>            -->
      <div
          v-if="activeCurrency === 'USDC'"
          class="address-wrapper"
      >
        <div class="qr-code__wrapper">
          <QRCodeVue3
              :width="275"
              :height="275"
              :value="address"
              :qrOptions="{ typeNumber: 0, mode: 'Byte', errorCorrectionLevel: 'H' }"
              :imageOptions="{ hideBackgroundDots: true, imageSize: 0.4, margin: 0 }"
              :dotsOptions="{
                    type: 'extra-rounded',
                    color: '#d9d6dc',
                  }"
              :backgroundOptions="{ color: 'transparent' }"
              :cornersSquareOptions="{ type: 'extra-rounded', color: '#d9d6dc' }"
              :cornersDotOptions="{ type: undefined, color: '#d9d6dc' }"
              myclass="my-qur"
              imgclass="img-qr"
          />
        </div>
        <div class="address">
          {{ shortenString(address, 10) }} <Copy :value="address" />
        </div>
      </div>
      <template v-else>
        <div class="divider" />
        <div class="actions-block">
          <BaseButton
            :disabled="topUpDisabled || !termsAccepted"
            grayscale-accent
            prefix
            @click="TopUpFromBank"
          >
            <template #prefix>
              <div class="bank-card-btn-prefix">
                <img src="@/assets/images/visa_logo.svg" alt="Visa" />
                <img src="@/assets/images/mastercard_logo.svg" alt="MasterCard" />
              </div>
            </template>
            <template #label>
              {{ text('wallet_topup.methods.bank_card') }}
            </template>
          </BaseButton>
        </div>
      </template>
    </div>
    <div
        v-if="!termsModel && dynamicTermsLink"
        class="terms_checkbox"
    >
      <label class="checkbox_container">
        <input type="checkbox" v-model="termsModel" />
        <span>{{ text('wallet_topup.accept_the') }}</span>
        <a :href="dynamicTermsLink" target="_blank">{{ text('wallet_topup.terms_of_use') }}</a>
      </label>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .wallet_topup {
    padding: 24px;
    position: relative;
    height: 100%;
    min-height: 600px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: 16px;

    .title {
      position: relative;

      color: var(--text-color-primary);
      font-family: "Rubik", sans-serif;
      font-size: 21px;
      font-style: normal;
      font-weight: 700;
      text-align: center;
      margin-bottom: 32px;

      .back-btn__wrapper {
        position: absolute;
        left: 0;
        top: 50%;
        transform: translateY(-50%);
      }
    }

    .input {
      display: flex;
      align-items: center;
      justify-content: space-between;

      background: #262329;
      margin: 32px auto 20px;
      padding: 16px;
      border-radius: 12px;
      border: 1px solid var(--element-bg);

      &:last-child {
        margin-bottom: 0;
      }

      span {
        color: var(--text-color-primary);
        font-family: "Rubik", sans-serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 135%;
        white-space: nowrap;
      }

      input {
        width: calc(100% - 70px);
        text-align: right;
        color: var(--text-color-primary);
        font-family: "Rubik", sans-serif;
        font-size: 18px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: 0.36px;
      }
    }

    .error {
      color: var(--text-color-error);
      text-align: center;
      font-family: "Rubik", sans-serif;
      font-size: 16px;
      font-style: normal;
      margin: 16px 0;
    }

    .divider {
      width: 100%;
      height: 1px;
      background: var(--layout-bg-lighter);
      margin-bottom: 26px;
    }

    .actions-block {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      .base-button {
        box-sizing: border-box;
        width: 100%;
      }

      &:has(.base-button:nth-of-type(2)) .base-button {
        width: calc(50% - 8px);
      }
    }

    .checkbox_container {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 24px 0;
    }

    .checkbox_container input[type="checkbox"] {
      margin: 0;
    }

    .payment_security_section {
      background-color: white;
      padding: 16px;
      margin: 0 24px 0 24px;
      border-radius: 12px;
    }

    .payment_methods {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      opacity: 0.8;
    }

    .payment_methods img {
      flex: 1;
      max-width: 35%;
      height: auto;
    }

    .checkbox_container {
      input {
        cursor: pointer;
      }
    }

    .bank-card-btn-prefix {
      display: flex;
      align-items: center;
      gap: 8px;
      padding-right: 5px;

      img {
        height: 16px;
      }
    }

    .address-wrapper {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 12px;

      margin: 12px 0 24px;

      .address {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 18px;
        font-weight: 500;
      }
    }

    @media (max-width: 920px) {
      padding: 16px;
    }
  }
</style>